import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import {
    Coordinate,
    ICustomSeriesPaneRenderer,
    LineStyle,
    PaneRendererCustomData,
    PriceToCoordinateConverter,
    Time
} from 'lightweight-charts'
import { PandFMarkerType, PandFSeriesOptions } from './options'
import { PandFData } from './data'

interface PandFBarItem {
    x: number;
    high: number;
    close: number;
    open: number;
    low: number,
    barCount: number
}


export class PandFSeriesRenderer<TData extends PandFData>
    implements ICustomSeriesPaneRenderer {
    _data: PaneRendererCustomData<Time, TData> | null = null
    _options: PandFSeriesOptions | null = null

    draw(
        target: CanvasRenderingTarget2D,
        priceConverter: PriceToCoordinateConverter
    ): void {
        target.useBitmapCoordinateSpace(scope =>
            this._drawImpl(scope, priceConverter)
        )
    }

    update(
        data: PaneRendererCustomData<Time, TData>,
        options: PandFSeriesOptions
    ): void {
        this._data = data
        this._options = options
    }

    _drawImpl(
        renderingScope: BitmapCoordinatesRenderingScope,
        priceToCoordinate: PriceToCoordinateConverter
    ): void {
        if (
            this._data === null ||
            this._data.bars.length === 0 ||
            this._data.visibleRange === null ||
            this._options === null
        ) {
            return
        }

        const options = this._options

        const { bars: items, visibleRange, barSpacing: barWidth } = this._data
        const { upColor, downColor, upMarker, downMarker, lineWidth } = this._options
        if (items.length === 0 || visibleRange.from >= items.length || visibleRange.to <= 0) {
            return
        }

        const ctx = renderingScope.context

        ctx.lineCap = 'butt'
        ctx.lineWidth = lineWidth * renderingScope.verticalPixelRatio
        ctx.lineJoin = 'round'

        const bars: PandFBarItem[] = this._data.bars.map(bar => {
            return {
                x: bar.x * renderingScope.horizontalPixelRatio,
                high: priceToCoordinate(bar.originalData.high)! * renderingScope.verticalPixelRatio,
                close: priceToCoordinate(bar.originalData.close)! * renderingScope.verticalPixelRatio,
                open: priceToCoordinate(bar.originalData.open)! * renderingScope.verticalPixelRatio,
                low: priceToCoordinate(bar.originalData.low)! * renderingScope.verticalPixelRatio,
                barCount: bar.originalData.barCount
            }
        })

        for (let i = visibleRange.from; i < visibleRange.to; i++) {
            const currentBar = bars[i]
            //close, open, high, low of bars represent y coordinates now. they are in ascendending format where as price points in scale are in descending 
            const up = currentBar.close <= currentBar.open
            this._drawMarkers(currentBar, ctx, barWidth, up ? upMarker : downMarker, up ? upColor : downColor)
        }
    }

    _drawMarkers(
        currentBar: PandFBarItem,
        ctx: CanvasRenderingContext2D,
        width: number,
        markerType: PandFMarkerType,
        style: CanvasRenderingContext2D['strokeStyle']
    ): void {
        //close, open, high, low of bars represent y coordinates now. they are in ascendending format where as price points in scale are in descending 
        const up = currentBar.close <= currentBar.open
        let markersCount = currentBar.barCount
        const height = Math.abs(currentBar.high - currentBar.low) / markersCount
        let y = up ? currentBar.open - (height / 2) : currentBar.open + (height / 2)
        while (markersCount > 0) {
            this._drawMarker(ctx, {
                x: currentBar.x as Coordinate,
                y: y as Coordinate
            }, width, height, markerType, style)
            if (up) y -= height
            else y += height
            markersCount--
        }

    }

    _drawMarker(
        ctx: CanvasRenderingContext2D,
        item: { x: Coordinate, y: Coordinate },
        width: number,
        height: number,
        markerType: PandFMarkerType,
        style: CanvasRenderingContext2D['strokeStyle']
    ): void {
        const halfWidth = width * 0.6
        const halfHeight = height * 0.4
        switch (markerType) {
            case PandFMarkerType.Cross: {
                ctx.beginPath()
                ctx.strokeStyle = style
                ctx.moveTo(item.x - halfWidth, item.y - halfHeight)
                ctx.lineTo(item.x + halfWidth, item.y + halfHeight)
                ctx.stroke()

                ctx.beginPath()
                ctx.strokeStyle = style
                ctx.moveTo(item.x - halfWidth, item.y + halfHeight)
                ctx.lineTo(item.x + halfWidth, item.y - halfHeight)
                ctx.stroke()

                break
            }
            case PandFMarkerType.Elliipse: {
                ctx.beginPath()
                ctx.strokeStyle = style
                ctx.ellipse(item.x, item.y, halfWidth, halfHeight, 0, 0, 2 * Math.PI)
                ctx.stroke()
                break
            }
            default:
                break
        }

    }

}

export function setLineStyle(ctx: CanvasRenderingContext2D, style: LineStyle): void {
    const dashPatterns = {
        [LineStyle.Solid]: [],
        [LineStyle.Dotted]: [ctx.lineWidth, ctx.lineWidth],
        [LineStyle.Dashed]: [2 * ctx.lineWidth, 2 * ctx.lineWidth],
        [LineStyle.LargeDashed]: [6 * ctx.lineWidth, 6 * ctx.lineWidth],
        [LineStyle.SparseDotted]: [ctx.lineWidth, 4 * ctx.lineWidth]
    }

    const dashPattern = dashPatterns[style]
    ctx.setLineDash(dashPattern)
}
